import * as Sentry from '@sentry/react';

/**
 * Web Vitals metrics we track
 */
export type WebVitalMetric = 'LCP' | 'FID' | 'CLS' | 'TTFB' | 'FCP' | 'INP';

/**
 * Web Vital measurement result
 */
export interface WebVitalMeasurement {
  name: WebVitalMetric;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
}

/**
 * Thresholds for Web Vitals ratings
 * Based on Google's Core Web Vitals thresholds
 */
const WEB_VITAL_THRESHOLDS: Record<WebVitalMetric, { good: number; poor: number }> = {
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  FID: { good: 100, poor: 300 }, // First Input Delay (ms)
  CLS: { good: 0.1, poor: 0.25 }, // Cumulative Layout Shift (score)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  INP: { good: 200, poor: 500 }, // Interaction to Next Paint (ms)
};

/**
 * Get the rating for a Web Vital metric
 */
export function getWebVitalRating(
  name: WebVitalMetric,
  value: number
): 'good' | 'needs-improvement' | 'poor' {
  const thresholds = WEB_VITAL_THRESHOLDS[name];
  if (!thresholds) return 'needs-improvement';

  if (value <= thresholds.good) return 'good';
  if (value >= thresholds.poor) return 'poor';
  return 'needs-improvement';
}

/**
 * Report a Web Vital measurement to Sentry
 */
export function reportWebVital(measurement: WebVitalMeasurement): void {
  // Sentry's browser tracing integration automatically captures Web Vitals
  // This function can be used for additional custom reporting if needed

  // Add as a custom measurement
  Sentry.setMeasurement(
    measurement.name,
    measurement.value,
    measurement.name === 'CLS' ? '' : 'millisecond'
  );

  // Add breadcrumb for visibility
  Sentry.addBreadcrumb({
    category: 'web-vital',
    message: `${measurement.name}: ${measurement.value.toFixed(2)} (${measurement.rating})`,
    level: measurement.rating === 'poor' ? 'warning' : 'info',
    data: {
      metric: measurement.name,
      value: measurement.value,
      rating: measurement.rating,
      delta: measurement.delta,
    },
  });
}

/**
 * Initialize Web Vitals tracking
 * Note: Sentry's browserTracingIntegration already captures Web Vitals automatically
 * This function provides additional utilities for custom tracking if needed
 */
export function initWebVitals(): void {
  // Sentry automatically tracks Web Vitals when browserTracingIntegration is enabled
  // This is a placeholder for any additional custom Web Vitals tracking logic

  // Log that Web Vitals tracking is active
  if (typeof window !== 'undefined') {
    console.log('[Observability] Web Vitals tracking enabled via Sentry');
  }
}

/**
 * Manually report a performance metric
 */
export function reportPerformanceMetric(
  name: string,
  value: number,
  unit: 'millisecond' | 'byte' | 'none' | '' = 'millisecond'
): void {
  Sentry.setMeasurement(name, value, unit);
}

/**
 * Start a performance span for custom timing
 */
export function startPerformanceSpan(
  name: string,
  op: string = 'custom'
): ReturnType<typeof Sentry.startInactiveSpan> {
  return Sentry.startInactiveSpan({
    name,
    op,
  });
}

/**
 * Create a timed operation wrapper
 */
export async function withTiming<T>(
  name: string,
  operation: () => Promise<T>,
  op: string = 'custom'
): Promise<T> {
  const span = startPerformanceSpan(name, op);

  try {
    const result = await operation();
    span?.end();
    return result;
  } catch (error) {
    span?.end();
    throw error;
  }
}
